/**
 * White-label brand scrub (DNKADM-0044). dink-admin resells a white-labeled
 * GoHighLevel as **Journix** — customers must NEVER see "GoHighLevel"/"GHL"
 * anywhere in the UI. Most occurrences in the codebase are code-internal
 * (endpoint paths, type discriminants, comments, test mocks) and stay; this
 * helper is the defensive guard for the few user-facing strings that come
 * **from the backend** and are rendered as-is (forwarded `*Error.ts` server
 * messages, the `connectionStatus.label`, humanized activity labels).
 *
 * `scrubBrand` is a PRESENTATION concern: apply it at the render/format
 * boundary only, never to mutate a non-displayed internal value (endpoint
 * path, type discriminant, `PickListError.picklist`, parsed data).
 *
 * Noted fragility: scrubbing backend copy client-side is belt-and-suspenders;
 * the real fix is removing the brand from dink-api's strings (deferred — see
 * the delivery's Architect notes). This is the agreed dink-admin-only round.
 */

/** The white-label replacement term shown to customers. */
const BRAND = 'Journix';

/**
 * The forbidden brand variants, longest/most-specific first so "GoHighLevel"
 * is consumed before the looser "High Level" can match a fragment of it:
 *  - `GoHighLevel` — the joined camelCase product name (case-insensitive).
 *  - `\bGHL\b` — the abbreviation as a whole word (so it never matches inside
 *    another word).
 *  - `High Level` — the spaced variant, whole-word, allowing flexible run of
 *    inter-word whitespace.
 *
 * Case-insensitive and global so multiple, mid-sentence occurrences are all
 * replaced. Surrounding text and punctuation are preserved.
 */
const BRAND_PATTERN = /GoHighLevel|\bGHL\b|\bHigh\s+Level\b/gi;

/**
 * Replace any forbidden brand term ("GoHighLevel"/"GHL"/"High Level") in a
 * rendered string with "Journix", case-insensitively and whole-word-aware,
 * preserving the surrounding text. A no-op on clean text; passes `null`
 * through unchanged (so it slots into the nullable `serverMessage` chain).
 */
export function scrubBrand(text: string | null): string | null {
  if (text === null) return null;
  return text.replace(BRAND_PATTERN, BRAND);
}

/**
 * The v2 `field_ownership` wire enum (DNKADM-0049/0058, handoff §4.0/§5.13). The
 * value `'GHL'` means the field is owned by the external contact system
 * (read-through, that system wins); `'DINK'` means it is managed here. These are
 * DATA discriminants — the type + its literals are DEFINED here, in the
 * allow-listed scrub-source module, so the raw `'GHL'` token never lives in
 * ordinary rendered/source code (the white-label guard treats `'GHL'` as a brand
 * literal everywhere else). Consumers (`memberProfileFields.ts`) import this type +
 * the safe default rather than spelling the literal themselves.
 */
export type FieldOwnership = 'GHL' | 'DINK';

/** All ownership values, in form-display order (the safe default first). */
export const FIELD_OWNERSHIPS: readonly FieldOwnership[] = ['GHL', 'DINK'] as const;

/**
 * The SAFE default ownership for a new field (handoff §5.13): the external contact
 * system owns it (read-through). Exported so callers never spell the `'GHL'` literal.
 */
const DEFAULT_FIELD_OWNERSHIP: FieldOwnership = 'GHL';

/**
 * Normalize a raw ownership value: anything but an explicit `'DINK'`
 * (case-insensitively) degrades to the safe {@link DEFAULT_FIELD_OWNERSHIP} — so a
 * malformed row is never silently treated as locally-owned. Lives here so the
 * `'GHL'` fallback literal stays in the allow-listed scrub-source module.
 */
export function normalizeFieldOwnership(value: unknown): FieldOwnership {
  return typeof value === 'string' && value.toUpperCase() === 'DINK'
    ? 'DINK'
    : DEFAULT_FIELD_OWNERSHIP;
}

/**
 * Brand-neutral display for the v2 `field_ownership` enum (DNKADM-0049, handoff
 * §4.0/§5.13). These are DATA values mapped to neutral labels so the brand NEVER
 * renders — the raw `'GHL'` must never reach the UI. (Defined here, the scrub
 * module, so the brand-neutral mapping lives alongside the other white-label render
 * seams.)
 */
export function fieldOwnershipLabel(ownership: FieldOwnership): string {
  return ownership === 'GHL' ? 'External / contact system' : 'Managed here';
}

/**
 * Brand-neutral label for the v2 `ghlCustomFieldKey` field (handoff §4.0/§5.4).
 * The field KEY is data routed to the contact system; its display label must be
 * generic, never "GHL …".
 */
export const CONTACT_SYSTEM_FIELD_KEY_LABEL = 'Contact-system field key';

/**
 * What the console calls the tenant's contact system (DNKADM-0499): the Settings > CRM nav
 * item, the CRM page title and its copy. The app has no per-tenant display name for the
 * contact system, so it is the generic "CRM" — never the raw brand.
 */
export const CRM_DISPLAY_NAME = 'CRM';

/**
 * The tenant's staff-calendar provider wire enum (DNKADM-0384 U1; dink-api
 * `StaffCalendarProvider`, DNKAPI-0504/0538): where a coach's confirmed bookings are
 * mirrored. `'none'` is OFF (the default — no coach syncs, whatever their own setting
 * says); `'ghl'` routes through the tenant's white-labeled sub-account calendars;
 * `'google'` writes directly to each coach's own Google Calendar. Exactly one is
 * active per tenant (a choice, not a pair of toggles).
 *
 * Like {@link FieldOwnership}, the type + its literals are DEFINED here, in the
 * allow-listed scrub-source module, so the raw brand token never lives in ordinary
 * source: consumers import the type, the parser and the labels rather than spelling
 * the literal themselves (the white-label guard treats it as a brand literal
 * everywhere else). These are DATA discriminants, never rendered as-is.
 */
export type StaffCalendarProvider = 'none' | 'ghl' | 'google';

/** All provider values, in form-display order (off first). */
export const STAFF_CALENDAR_PROVIDERS: readonly StaffCalendarProvider[] = [
  'none',
  'ghl',
  'google',
] as const;

/**
 * Normalize a raw provider wire value (case-insensitive): anything but a known
 * provider degrades to `'none'` (off) — a malformed or absent value never reads as
 * "syncing somewhere". Lives here so the brand literal stays in the scrub module.
 */
export function normalizeStaffCalendarProvider(value: unknown): StaffCalendarProvider {
  if (typeof value !== 'string') return 'none';
  const v = value.trim().toLowerCase();
  return v === 'ghl' || v === 'google' ? v : 'none';
}

/**
 * The plain-words option label for a provider (DNKADM-0384 U1): "Off", "Through
 * Journix" (the white-label name this console already uses for the contact/booking
 * platform, e.g. "no active Journix integration"), or "Direct to Google Calendar".
 * Never an id, never a code, never the raw brand.
 */
export function staffCalendarProviderLabel(provider: StaffCalendarProvider): string {
  if (provider === 'ghl') return `Through ${BRAND}`;
  if (provider === 'google') return 'Direct to Google Calendar';
  return 'Off';
}

/**
 * One sentence under each provider option saying what it means for a coach
 * (DNKADM-0384 U1). Brand-neutral apart from the white-label name itself.
 */
export function staffCalendarProviderDescription(provider: StaffCalendarProvider): string {
  if (provider === 'ghl') {
    return `Bookings go to the coach’s calendar in your ${BRAND} account, which carries them to the coach’s own calendar.`;
  }
  if (provider === 'google') {
    return 'Bookings go straight to each coach’s own Google Calendar. Each coach connects their Google account once.';
  }
  return 'No coach calendar syncs, whatever each coach’s own setting says.';
}
